import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../../context/AuthContext';

export interface StockProduct {
  id: number;
  code: string;
  name: string;
  category: string;
  price: number;
  stock: number;
  min_stock: number;
  is_low: number;
}

interface Props {
  value: string;
  onSelect: (p: StockProduct) => void;
  onChange: (name: string) => void;
  placeholder?: string;
  showStock?: boolean;  // 재고 수량 표시 여부
  warnOnLow?: boolean;  // 재고 부족 경고
}

export default function ProductSearch({
  value, onSelect, onChange, placeholder = '품명 입력 또는 검색', showStock = true, warnOnLow = false,
}: Props) {
  const [query,    setQuery]    = useState(value);
  const [results,  setResults]  = useState<StockProduct[]>([]);
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState<StockProduct | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  /* 외부 클릭 닫기 */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* 검색 */
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      const res = await authFetch(`/api/inventory?search=${encodeURIComponent(query)}`);
      if (res.ok) setResults(await res.json());
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const handleInput = (v: string) => {
    setQuery(v);
    onChange(v);
    setSelected(null);
    setOpen(true);
  };

  const handleSelect = (p: StockProduct) => {
    setQuery(p.name);
    setSelected(p);
    setOpen(false);
    onSelect(p);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <input
          value={query}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => query && setOpen(true)}
          className="w-full border-0 bg-transparent focus:outline-none focus:bg-amber-50 rounded px-1 py-0.5 text-sm"
          placeholder={placeholder}
        />
        {loading && (
          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-stone-400 animate-pulse">…</span>
        )}
      </div>

      {/* 재고 부족 경고 */}
      {warnOnLow && selected && selected.is_low === 1 && (
        <p className="text-xs text-red-500 mt-0.5">재고 부족 (현재 {selected.stock}개)</p>
      )}

      {/* 드롭다운 */}
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 top-full mt-1 w-72 bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden">
          {results.slice(0, 8).map(p => (
            <button
              key={p.id}
              type="button"
              onMouseDown={() => handleSelect(p)}
              className="w-full text-left px-3 py-2.5 hover:bg-amber-50 border-b border-stone-50 last:border-0 flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{p.name}</p>
                <p className="text-xs text-stone-400">{p.code} · {p.category}</p>
              </div>
              {showStock && (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                  p.is_low ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                  {p.stock}개
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
